import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { fileHandler, mapRowData } from 'utils/excelRenderer';
import AffiliatesAPI from 'api/AffiliatesAPI';
import { toBase64 } from 'utils/fileHelper';

interface IUploadFile {
  setUploadMode: any;
  affiliate: any;
}

const UploadFile: React.FC<IUploadFile> = ({ setUploadMode, affiliate }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [ file, setFile ] = useState<any>([]);
  const [ rows, setRows ] = useState<any>([]);
  const [ cols, setCols ] = useState<any>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(async(accptFile) => {
      const reader = new FileReader();
      reader.onabort = () => {
        enqueueSnackbar('File reading aborted', { variant: 'info' });
      };
      reader.onerror = () => {
        enqueueSnackbar('Error reading file', { variant: 'error' });
      };

      setFile(accptFile);
      const { rows } = await fileHandler(accptFile);
      const colNames: string[] = rows[0];
      const rowData = rows.slice(1);
      const mappedRowData = mapRowData(colNames, rowData);
      setRows(mappedRowData);
      setCols(colNames);
    });
  }, []);

  const fileValidator = (file: any) => {
    if (file.size > 20000000) {
      enqueueSnackbar('Your file is too large, upload a file less than 20mbs', { variant: 'error' });
      return {
        code: 'size-too-large',
        message: 'file is larger than 20MB',
      };
    }
    return null;
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet , text/csv',
  });

  const removeFile = () => {
    setFile([]);
    setRows([]);
    setCols([]);
  };

  const saveUpload = async(file: any) => {
    let fileInfo = {};
    const base64Str = await toBase64(file);
    fileInfo = {
      uploadedFile: {
        base64: base64Str,
        name: file?.path,
        dateAdded: Date.now()
      },
    };
    const docUpdate = affiliate.data.documents;
    docUpdate.push(fileInfo);
    const res = await AffiliatesAPI.updateAffiliate({
      _id: affiliate.data._id,
      documents: docUpdate
    });
    if (res?.success) {
      enqueueSnackbar('File uploaded!', { variant: 'success' });
      setUploadMode(false);
    }
  };

  return (
    <div className='upload-aff-file-container'>
      <div className='upload-aff-file-header'>
        <div className='upload-aff-file-header-content'>
          <div className='upload-aff-file-header-group'>
            <div className='upload-aff-file-header-primary'>
              Upload and attach a file
            </div>
            <div className='upload-aff-file-header-secondary'>
              Upload and attach a file to this affiliation to be shared with members.
            </div>
          </div>
        </div>
        <div className='upload-aff-file-header-close' onClick={() => setUploadMode(false)}>
          <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path d='M18 6L6 18M6 6L18 18' stroke='#BDBEC1' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
          </svg>
        </div>
      </div>
      <div className='upload-aff-file-body'>
        <div className='upload-aff-file-body-frame'>
          <div {...getRootProps()} style={{ width: '100%' }}>
            <div className={`files-container-empty ${file?.name ? 'file-uploaded' : ''}`}>
              {file?.name ? (<>
                <p className='files-container-text'>
                  <span className='bill-container-fit-screen-content-row-highlight-blue cursor-pointer'>{file.name} </span>
                  <svg onClick={removeFile} width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <g id='trash-03'>
                      <path id='Icon' d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167' stroke='#BDBEC1' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                    </g>
                  </svg>
                </p>
              </>
              ) : (
                <>
                  <p className='files-container-text'>
                    <span className='bill-container-fit-screen-content-row-highlight-blue cursor-pointer'>Click to upload</span> or drag and drop</p>
                  <p className='files-container-text'>CSV file format only (max 200 MB)</p>
                  <input {...getInputProps()} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='upload-aff-file-footer'>
        <div className='upload-aff-file-footer-content'>
          <div className='upload-aff-file-footer-cancel' onClick={() => setUploadMode(false)}>
            <div className='upload-aff-file-footer-cancel-text'>
              Cancel
            </div>
          </div>
          <div className='upload-aff-file-footer-done' onClick={() => saveUpload(file)}>
            <div className='upload-aff-file-footer-done-text'>
              Done
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadFile;
